import axios from 'axios';
import config from 'commons-vehicle/config/index.js';
import { map, intersection, isEmpty, filter, sumBy, isObjectLike } from 'lodash';
import { HTTPService, UrlService } from '@sm360/widget-commons-core-services';
import { CookieService, COOKIES, ReduxClient } from '@sm360/widget-commons-general';

import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import { DATA_TYPES } from 'commons-vehicle/constants/dataTypes.js';
import PURCHASE_METHODS from 'commons-vehicle/PurchaseMethods/constants/purchaseMethodsValues.js';
import { LEASE, FINANCE, CASH_PURCHASE } from 'commons-vehicle/PurchaseMethods/constants/purchaseMethods.js';
import * as WARNINGS from 'commons-vehicle/Quote/constants/warnings.js';
import { CHECKOUT_WITH_DEPOSIT } from 'commons-vehicle/Quote/constants/statuses.js';
import {
    resetPurchaseMethodsFromOriginData,
    selectPurchaseMethod,
    selectTerm,
    selectPaymentFrequency,
    setCashDown,
    setTradeIn,
    setLien,
    selectKMPlan,
    resetPurchaseMethodsOptionsFromQuoteItems,
    resetOptionsAmounts,
    setDeposit,
} from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';
import { resetVehicleFromShowroomQuoteVehicle } from 'commons-vehicle/Vehicle/services/VehicleService/VehicleService.js';
import ShowroomService from 'commons-vehicle/services/ShowroomService/ShowroomService.js';

const criticalWarnings = [
    WARNINGS.RETAIL_MISMATCH,
    WARNINGS.PAYMENT_MISMATCH,
    WARNINGS.PAYMENT_TAXES_MISMATCH,
    WARNINGS.FINANCED_AMOUNT_MISMATCH,
    WARNINGS.FINANCED_AMOUNT_WITH_TAXES_MISMATCH,
    WARNINGS.DUE_AT_DELIVERY_MISMATCH,
];

class QuoteService {
    constructor() {
        this.basePath = `${config.endpoint.showroom}/service/v1`;
    }

    saveQuote(quote, conf) {
        if (!conf || !quote) {
            return Promise.reject(HTTPService.formatRejectResponse('missing parameters'));
        }

        const orgId = conf.organizationId;
        const orgUnitId = conf.organizationUnitId;
        const location = conf.location;
        const context = `refresh=1&location=${location}&datalevel=1&context=${orgId}.${orgUnitId}&userName=public&password=public`;

        return ShowroomService.getPublicToken().then((response) => {
            const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);

            return axios.put(`${this.basePath}/Quote?${context}`, quote, {
                headers: {
                    'x-jwt': userCookie || response.data,
                },
            });
        });
    }

    getQuote(quoteId) {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);

        return axios.get(`${this.basePath}/Quote/${quoteId}?datalevel=1`, {
            headers: {
                'x-jwt': token,
            },
        });
    }

    getQuoteIds() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);

        return axios.get(`${this.basePath}/Quotes`, {
            headers: {
                'x-jwt': token,
            },
        });
    }
}

export function resetQuoteFromModelYear(quote, location, leadType, modelYear) {
    return quote
        .set('location', location)
        .set('leadType', leadType)
        .set('vehicle', modelYear.getVehicleFromConfiguration(quote.vehicle));
}

export function resetQuoteFromVehicle(quote, location, leadType, vehicle) {
    return quote
        .set('location', location)
        .set('leadType', leadType)
        .set('vehicle', vehicle);
}

export function resetQuoteFromVehicleV25(quote, province, vehicle) {
    return quote.set('province', province).set('vehicle', vehicle);
}

export function resetQuoteFromShowroomSave(quote, showroomQuote) {
    const warningCodes = map(showroomQuote.warnings, (warning) => {
        return warning.error;
    });
    const availableItems =
        showroomQuote.availableItems ||
        quote.vehicle.purchaseMethods.availableOptions.map((option) => option.getShowroomQuoteItem());
    const writeToken = UrlService.getUrlParameter('writeToken');

    if (!isEmpty(writeToken) && showroomQuote.accountId === 'public') {
        showroomQuote.securityToken = writeToken;
    }

    return quote
        .set('id', showroomQuote.id)
        .set('availableItems', showroomQuote.availableItems)
        .set('revision', showroomQuote.__revision) // eslint-disable-line no-underscore-dangle
        .set('accountId', showroomQuote.accountId)
        .set('leadId', showroomQuote.leadId)
        .set('dealerId', showroomQuote.dealerId)
        .set('isValid', isEmpty(intersection(criticalWarnings, warningCodes)))
        .set('showroomItems', showroomQuote.items)
        .set('warnings', showroomQuote.warnings)
        .set('errors', showroomQuote.errors)
        .set('securityToken', showroomQuote.securityToken || '')
        .set('modified', false)
        .set('vehicleId', String(showroomQuote.vehicleId || ''))
        .setIn(
            ['vehicle', 'purchaseMethods'],
            resetOptionsAmounts(
                resetPurchaseMethodsOptionsFromQuoteItems(
                    quote.vehicle.purchaseMethods,
                    showroomQuote.items,
                    availableItems
                )
            )
        );
}

export function resetQuoteFromShowroomSaveV25(quote, showroomQuoteId) {
    return quote.set('id', showroomQuoteId);
}

export function resetQuoteFromShowroomLoad(quote, showroomQuote, disclaimers) {
    const { pricingSummary, financingSummary, tradeIns, purchaseMethod, purchaseMethods } = showroomQuote;

    let vehicle = resetVehicleFromShowroomQuoteVehicle(showroomQuote);

    if (!isEmpty(purchaseMethods)) {
        let selectedPurchaseMethod = CASH_PURCHASE;
        if (purchaseMethod === PURCHASE_METHODS.LEASE || purchaseMethod === PURCHASE_METHODS.SINGLE_PAYMENT_LEASE) {
            selectedPurchaseMethod = LEASE;
        } else if (
            purchaseMethod === PURCHASE_METHODS.FINANCE ||
            purchaseMethod === PURCHASE_METHODS.FINANCE_WITH_BALLOON
        ) {
            selectedPurchaseMethod = FINANCE;
        }

        let quotePurchaseMethods = selectPurchaseMethod(
            resetPurchaseMethodsFromOriginData(DataOrigins.VEHICLE_PAYMENTS, purchaseMethods, null, disclaimers),
            selectedPurchaseMethod
        );
        if (selectedPurchaseMethod !== CASH_PURCHASE) {
            quotePurchaseMethods = selectTerm(quotePurchaseMethods, financingSummary.term);
            quotePurchaseMethods = selectPaymentFrequency(quotePurchaseMethods, financingSummary.paymentsPerYear);
            quotePurchaseMethods = setCashDown(quotePurchaseMethods, pricingSummary.cashDown);
            if (financingSummary.annualKMAllowed > 0) {
                quotePurchaseMethods = selectKMPlan(quotePurchaseMethods, financingSummary.annualKMAllowed);
            }
        }
        quotePurchaseMethods = setTradeIn(quotePurchaseMethods, tradeIns.amountSummary.tradeIn);
        quotePurchaseMethods = setLien(quotePurchaseMethods, tradeIns.amountSummary.lien);

        vehicle = vehicle.set('purchaseMethods', quotePurchaseMethods);
    }

    const newQuote = quote
        .set('location', showroomQuote.location)
        .set('status', showroomQuote.status)
        .set('creditApplicationId', showroomQuote.creditApplicationId)
        .set('vehicleId', String(showroomQuote.vehicleId || ''))
        .set('vehicle', vehicle);

    return resetQuoteFromShowroomSave(newQuote, showroomQuote);
}

export function resetQuoteDepositLead(quote, depositResponse) {
    return quote
        .set('status', quote.status | CHECKOUT_WITH_DEPOSIT)
        .set('leadId', String(depositResponse.leadId))
        .set('creditCardChargeId', depositResponse.stripeChargeId)
        .setIn(
            ['vehicle', 'purchaseMethods'],
            setDeposit(quote.vehicle.purchaseMethods, {
                checkout: true,
                override: depositResponse.depositAmount,
            })
        );
}

export function resetQuoteCreditApplication(quote, creditApplicationId) {
    return quote.set('creditApplicationId', creditApplicationId);
}

export function getEmployeeRebateFromShowroomQuote(showroomQuote) {
    if (!isObjectLike(showroomQuote) || isEmpty(showroomQuote) || isEmpty(showroomQuote.items)) {
        return null;
    }

    const employeeRebates = filter(
        showroomQuote.items,
        (item) => item.dataType === DATA_TYPES.REBATE.DATA_TYPE_ID && item.typeID === DATA_TYPES.REBATE.TYPES.EMPLOYEE
    );

    if (isEmpty(employeeRebates)) {
        return null;
    }

    return {
        description: employeeRebates[0].description.descriptions[0],
        rebate: sumBy(employeeRebates, (employeeRebate) => employeeRebate.amounts.retail),
    };
}

export function quoteHasVehicle(quote) {
    return (
        quote && quote.vehicle && quote.vehicle.year && !isEmpty(quote.vehicle.make) && !isEmpty(quote.vehicle.model)
    );
}

export function isQuoteInGarageV25(quoteId) {
    const storeRedux = ReduxClient.getOrCreate();
    const state = storeRedux.getState();
    return state.quotes && state.quotes.find((quote) => quote.id === quoteId);
}

export default {
    QuoteService: new QuoteService(),
    resetQuoteFromModelYear,
    resetQuoteFromVehicle,
    resetQuoteFromVehicleV25,
    resetQuoteFromShowroomSave,
    resetQuoteFromShowroomSaveV25,
    resetQuoteFromShowroomLoad,
    resetQuoteDepositLead,
    resetQuoteCreditApplication,
    getEmployeeRebateFromShowroomQuote,
    quoteHasVehicle,
    isQuoteInGarageV25,
};
